import Phaser from 'phaser'
import Config from '../launch/config'
import Effects from '../effects/effects'
// import Sensor from '../sensor/sensor'
import Matter from 'matter-js'

import formation from './formation'
import healthBar from './health-bar'
import move from './move'
import orbit from './orbit'
import pointerDown from './pointer-down'
import shoot from './shoot'
// import {isDebug} from '../launch/utils'
import {saveGame} from '../launch/store'
import {_addToEventLog, forceAppUpdate} from '../app/app'
import {gameOverCheck} from '../game-over/utils'
import audio from '../launch/audio'

export default class Ship {
    constructor(props) {
        const {
            angle,
            faction,
            diameter,
            health,
            id,
            matterConfig,
            name,
            orbitingEntity,
            proximityRange,
            rank,
            scale,
            selectedTexture,
            sensorRange,
            texture,
            type,
            x,
            y,
        } = props

        const chance = Config.chance
        const difficulty = Config.difficultyLevels[Config.difficulty]

        this.type = type || 'ship'
        this.formation = formation
        this.orbit = orbit
        this.move = move
        this.handlePointerdown = pointerDown
        this.healthBar = healthBar
        this.rank = rank || 0
        this.shoot = shoot
        this.id = id || chance.guid()
        this.name =
            name ||
            chance.capitalize(
                chance.pick([
                    chance.word({syllables: 1}),
                    chance.word({syllables: 2}),
                    chance.word({syllables: 3}),
                ])
            )
        this.faction = faction
        this.isDetected =
            this.faction === Config.userFactionName || Config.isDemo
        this.diameter = diameter || 10
        this.health = health || Config.shipMaxHealth
        this.selected = false
        this.moving = false
        this.fighting = false
        this.enteringOrbit = false
        this.leavingOrbit = false
        this.orbitingEntity = orbitingEntity || null
        this.scale = scale || 0.3
        this.speed =
            this.faction === Config.userFactionName
                ? Config.shipRanks[`rank${this.rank}`].shipSpeed
                : difficulty.enemyShipSpeed
        this.orbitAngle = 0
        this.orbitSpeed = 0.004
        this.formationRadius = this.diameter + 10
        this.proximityRange =
            proximityRange ||
            this.diameter +
                Config.shipRanks[`rank${this.rank}`].shipProximityRange
        this.sensorRange =
            sensorRange ||
            this.diameter + Config.shipRanks[`rank${this.rank}`].shipSensorRange
        this.constraints = {}
        this.towedTowables = {}
        this.towLines = {}

        this.x =
            x ||
            Phaser.Math.Between(
                Config.boardPadding,
                Config.boardSizes[Config.boardSize].boardWidth -
                    Config.boardPadding
            )
        this.y =
            y ||
            Phaser.Math.Between(
                Config.boardPadding,
                Config.boardSizes[Config.boardSize].boardHeight -
                    Config.boardPadding
            )

        // Texture
        // TODO: sort this out using config instead
        const enemyTextureMap = {
            0: 'shipRed',
            1: 'shipOrange',
            2: 'shipBlue',
            3: 'shipGreen',
        }
        const isEnemy = this.faction?.includes(Config.enemyFactionName)
        let defaultTexture = 'shipGreen'

        if (isEnemy) {
            defaultTexture = enemyTextureMap[this.faction.at(-1)] || 'shipRed'
        }

        // Rank-based
        if (this.rank > 0 && !isEnemy) {
            defaultTexture = `shipGreenRank${this.rank}`
        }
        this.texture = texture || defaultTexture
        this.selectedTexture = selectedTexture || 'shipWhite'

        this.sprite = Config.scene.matter.add.sprite(this.x, this.y)
        this.sprite.setTexture(this.texture)
        this.sprite.setScale(this.scale)
        this.sprite.setDepth(7)

        if (this.type === 'satellite') {
            this.sprite.setCircle(this.diameter + 4)
        } else {
            this.sprite.setCircle(this.diameter + 2)
            this.sprite.originX = this.sprite.originX - 0.07
            angle && Matter.Body.setAngle(this.sprite.body, angle)
        }

        this.matterConfig = Object.assign(
            {
                density: 0.1, // default 0.001
                friction: 1, // default 0.1
                frictionAir: 0.03, // defafult 0.01
                // frictionStatic: 8, // default 0.5
                restitution: 0.1, // default 0
                sleepThreshold: 10, // default 60
            },
            matterConfig || {}
        )
        Matter.Body.set(this.sprite.body, this.matterConfig)

        this.sprite.body[Config.namespace] = {
            isShipBody: true,
            shipId: this.id,
        }
        this.sprite[Config.namespace] = {type: 'ship'}

        // Matter bodies as sensors
        // Repositioning matter sensors in update loop lags hard
        // this.sensorBody = Config.scene.matter.add.circle(
        //     this.x,
        //     this.y,
        //     this.sensorRange,
        //     {
        //         isSensor: true,
        //     }
        // )
        // this.sensorBody[Config.namespace] = {
        //     isShipSensorBody: true,
        //     shipId: this.id,
        // }

        // this.proximityBody = Config.scene.matter.add.circle(
        //     this.x,
        //     this.y,
        //     this.proximityRange,
        //     {
        //         isSensor: true,
        //         // Keep proxximity sensor awake at all times
        //         // Fixes sleep overlap behavior
        //         sleepThreshold: -1,
        //     }
        // )
        // this.proximityBody = Config.scene.matter.add.polygon(
        //     this.x,
        //     this.y,
        //     sides,
        //     this.proximityRange,
        //     {
        //         isSensor: true,
        //         // Keep proxximity sensor awake at all times
        //         // Fixes sleep overlap behavior
        //         sleepThreshold: -1,
        //     }
        // )
        // this.proximityBody[Config.namespace] = {
        //     isShipProximityBody: true,
        //     shipId: this.id,
        // }

        // Geom Circles for ship CircleToCircle detection
        this.shipCircle = new Phaser.Geom.Circle(this.x, this.y, this.diameter)
        this.shipCircle[Config.namespace] = {type: 'shipCircle'}
        // Draw sensor circle
        // this.shipCircleGraphics = Config.scene.add.graphics({
        //     lineStyle: {width: 1, color: 0xffffff, alpha: 1},
        // })
        // this.shipCircleGraphics.strokeCircleShape(this.shipCircle)
        // this.shipCircleGraphics[Config.namespace] = {type: 'shipCircleGraphics'}

        this.sensorCircle = new Phaser.Geom.Circle(
            this.x,
            this.y,
            this.sensorRange
        )
        this.sensorCircle[Config.namespace] = {type: 'sensorCircle'}

        // Draw sensor circle
        // this.sensorGraphics = Config.scene.add.graphics({
        //     lineStyle: {width: 1, color: 0xffffff, alpha: 1},
        // })
        // this.sensorGraphics.strokeCircleShape(this.sensorCircle)
        // this.sensorGraphics[Config.namespace] = {type: 'sensorGraphics'}

        this.proximityCircle = new Phaser.Geom.Circle(
            this.x,
            this.y,
            this.proximityRange
        )
        this.proximityCircle[Config.namespace] = {type: 'proximityCircle'}

        // Draw proximity circle
        // this.proximityGraphics = Config.scene.add.graphics({
        //     lineStyle: {width: 1, color: 0xffffff, alpha: 1},
        // })
        // this.proximityGraphics.strokeCircleShape(this.proximityCircle)
        // this.proximityGraphics[Config.namespace] = {type: 'proximityGraphics'}

        // Label
        // if (this.faction === Config.userFactionName) {
        //     this.labelWidth = 50
        //     this.labelXoffset = this.labelWidth / 2
        //     this.label = Config.scene.add.text(
        //         this.labelXoffset,
        //         this.y + this.diameter + 4,
        //         this.name,
        //         {
        //             fontFamily: 'Roboto, sans-serif',
        //             fontSize: '10px',
        //             color: '#fff',
        //             align: 'center',
        //             fixedWidth: this.labelWidth,
        //             // backgroundColor: `#${Config.colorRed}`,
        //         }
        //     )
        //     this.label.setShadowFill(true)
        //     this.label.setShadowOffset(1, 2)
        //     this.label.setResolution(window.devicePixelRatio)
        //     this.label.depth = 8
        //     this.label[Config.namespace] = {type: 'ship label'}
        // }

        // Weapon fire timer, to be paused/unpaused on ship proximity overlap
        this.weaponSpeed = Config.shipRanks[`rank${this.rank}`].shipWeaponSpeed

        if (this.faction.includes(Config.enemyFactionName)) {
            this.weaponSpeed =
                Config.difficultyLevels[Config.difficulty].enemyShipWeaponSpeed
        }

        this.weaponTargets = {}
        this.weaponTimer = Config.scene.time.addEvent({
            delay: this.weaponSpeed,
            callback: () => {
                const targetKeys = Object.keys(this.weaponTargets)

                if (targetKeys.length) {
                    // Shoot first acquired target
                    const otherShip = Config.ships[targetKeys[0]]
                    this.shoot(otherShip)

                    // TODO: Multi-target upgrade
                    // targetKeys.forEach((key) => {
                    //     const target = this.weaponTargets[key]
                    //     this.shoot(target.x, target.y)
                    // })
                }
            },
            loop: true,
            paused: true,
        })

        // Health bar
        this.healthBarRect = Config.scene.add.rectangle(
            x,
            y,
            this.health / 4,
            3,
            0xffffff
        )
        this.healthBarRect.setDepth(5)
        this.healthBarRect[Config.namespace] = {type: 'healthBarRect'}

        // Thrust
        // if (this.type !== 'satellite') {
        //     this.thrustParticles = Config.scene.add.particles('particleWhite')
        //
        //     this.thrustEmitter = this.thrustParticles.createEmitter({
        //         speed: {min: 0, max: 300},
        //         lifespan: 100,
        //         scale: {start: 0.15, end: 0},
        //         blendMode: 'ADD',
        //         follow: this.sprite,
        //         quantity: 2,
        //     })
        //     this.thrustEmitter.stop()
        // }

        if (orbitingEntity) {
            orbitingEntity.orbitingShips[this.id] = {
                faction: this.faction,
                isDetected: this.isDetected,
            }
        }

        Config.ships[this.id] = this
    }

    update(time, delta) {
        const body = this.sprite.body

        if (!body) {
            return
        }

        const difficulty = Config.difficultyLevels[Config.difficulty]
        const isEnemy = this.faction !== Config.userFactionName

        this.x = body.position.x
        this.y = body.position.y

        // this.sensorBody.position = {x: this.x, y: this.y}
        // this.proximityBody.position = {x: this.x, y: this.y}
        this.shipCircle.setPosition(this.x, this.y)
        this.sensorCircle.setPosition(this.x, this.y)
        this.proximityCircle.setPosition(this.x, this.y)

        // Active state texture
        this.sprite.setTexture(
            this.selected ? this.selectedTexture : this.texture
        )

        // if (this.label) {
        //     this.label.setVisible(this.selected)
        //     this.label.x = this.x - this.labelXoffset
        //     this.label.y = this.y + this.diameter + 4
        // }

        this.fighting = false
        this.weaponTimer.paused = true

        // TODO: performance issues
        // this.formation()
        this.move()
        this.orbit()
        this.healthBar()

        // Update tow lines
        if (this.towLines && Object.keys(this.towLines).length) {
            Object.keys(this.towLines).forEach((key) => {
                const towLine = this.towLines[key]

                towLine.line.setTo(
                    body.position.x,
                    body.position.y,
                    Config.towables[key].sprite.body.position.x,
                    Config.towables[key].sprite.body.position.y
                )

                towLine.graphics.clear()
                towLine.graphics.strokeLineShape(towLine.line)
            })
        }

        if (isEnemy) {
            this.sprite.setVisible(Config.isDemo ? true : this.isDetected)
            this.isDetected = false
            // if (this.thrustEmitter) {
            //     if (this.isDetected && !this.orbitingEntity && this.moving) {
            //         this.thrustEmitter.start()
            //     } else {
            //         this.thrustEmitter.stop()
            //     }
            // }
        } else {
            // if (this.moveLineGraphics) {
            //     this.moveLineGraphics.alpha = this.selected
            //         ? Config.moveLineSelectedAlpha
            //         : Config.moveLineAlpha
            // }
            if (this.moveMarker?.body) {
                Matter.Body.rotate(this.moveMarker.body, 0.1)
                this.moveMarker.alpha = this.selected
                    ? Config.moveMarkerSelectedAlpha
                    : Config.moveMarkerAlpha
            }
        }

        // Make sure ship doesn't sleep while selected
        if (this.selected) {
            Matter.Sleeping.set(this.sprite.body, false)
        }

        // Destroy ship
        if (this.health <= 0) {
            new Effects().explode(this.x, this.y, this.faction)

            if (isEnemy) {
                // Rank up victor
                const targetKeys = Object.keys(this.weaponTargets)

                if (targetKeys.length) {
                    const victorShip = Config.ships[targetKeys[0]]

                    if (
                        victorShip?.rank < 3 &&
                        !victorShip.faction.includes(Config.enemyFactionName)
                    ) {
                        victorShip.rank += 1
                        victorShip.weaponSpeed =
                            Config.shipRanks[
                                `rank${victorShip.rank}`
                            ].shipWeaponSpeed

                        if (victorShip.type === 'satellite') {
                            victorShip.texture = `satelliteGreenRank${victorShip.rank}`
                        } else {
                            victorShip.texture = `shipGreenRank${victorShip.rank}`
                        }
                    }
                }

                Config.gameStats.shipsDestroyed += 1
                Config.gameStats.score += difficulty.shipDestroyPoints
                new Effects().scoreAnim({
                    x: this.x,
                    y: this.y,
                    num: difficulty.shipDestroyPoints,
                    positive: true,
                })

                Config.gameStats.resources += difficulty.shipResourceSalvage
                setTimeout(() => {
                    new Effects().resourceAnim({
                        x: this.x,
                        y: this.y,
                        num: difficulty.shipResourceSalvage,
                        positive: true,
                    })
                }, Config.pointAnimDelay)

                _addToEventLog({
                    html: `An <span style="color: #${Config.colorRed}">enemy ${this.type}</span> has been destroyed`,
                    coords: {x: this.x, y: this.y},
                })
            } else {
                Config.gameStats.shipsLost += 1
                Config.gameStats.score -= difficulty.shipDestroyPoints
                new Effects().scoreAnim({
                    x: this.x,
                    y: this.y,
                    num: difficulty.shipDestroyPoints,
                    positive: false,
                })

                let html = `The ship <span style="color: #${Config.colorGreen}">${this.name}</span> has been destroyed`

                if (this.type === 'satellite') {
                    html = `A <span style="color: #${Config.colorGreen}">satellite</span> has been destroyed`
                }

                _addToEventLog({
                    html,
                    coords: {x: this.x, y: this.y},
                })
            }
            this.destroy()

            forceAppUpdate()
            saveGame('destroy ship')
        }

        this.weaponTargets = {}
    }

    detachTowables() {
        if (Object.keys(this.towedTowables).length) {
            Object.keys(this.towedTowables).forEach((key) => {
                const towable = Config.towables[key]

                // towable.sprite.body.angle = 0
                Matter.Body.rotate(towable.sprite.body, 0)

                towable.isBeingTowed = false
                towable.towShipId = null
                towable.constraint &&
                    Config.scene.matter.world.removeConstraint(
                        towable.constraint
                    )
            })
        }

        if (Object.keys(this.towLines).length) {
            Object.keys(this.towLines).forEach((key) => {
                const towLine = this.towLines[key]
                towLine.graphics.clear()
                towLine.graphics.destroy()
            })
        }
    }

    destroy() {
        audio.shipDestroy()
        Matter.Body.setVelocity(this.sprite.body, {
            x: 0,
            y: 0,
        })

        this.detachTowables()
        // this.moveLineGraphics && this.moveLineGraphics.destroy()
        this.moveMarker && this.moveMarker.destroy()
        this.moving = false
        // this.thrustParticles && this.thrustParticles.destroy()
        // this.label && this.label.destroy()
        // Config.scene.matter.world.remove(this.sensorBody)
        // Config.scene.matter.world.remove(this.proximityBody)
        Config.scene.matter.world.remove(this.shipCircle)
        Config.scene.matter.world.remove(this.sensorCircle)
        Config.scene.matter.world.remove(this.proximityCircle)
        Config.scene.matter.world.remove(this.sprite)
        this.weaponTimer.destroy()
        this.healthBarRect.destroy()
        this.sprite.destroy()

        if (this.orbitingEntity) {
            delete this.orbitingEntity.orbitingShips[this.id]
        }
        delete Config.ships[this.id]
        delete Config.selectedShips[this.id]

        gameOverCheck('ship destroy')
    }
}
